// Structured Data (JSON-LD) generators for SEO

export const SITE_URL = 'https://getsentinel.pro';
export const SITE_NAME = 'Sentinel';
export const SITE_LOGO = `${SITE_URL}/logo.png`;

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": SITE_LOGO,
    "description": "Enterprise-grade AI financial intelligence platform for duplicate payment prevention and automated reconciliation.",
    "foundingDate": "2025",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "url": `${SITE_URL}/support`
    }
  };
}

export function generateSoftwareApplicationSchema(pricing?: { free?: boolean; price?: number }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": SITE_NAME,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": pricing?.price || 0,
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "500",
      "bestRating": "5",
      "worstRating": "1"
    },
    "description": "Enterprise-grade financial intelligence platform for duplicate payment prevention, automated reconciliation, and cash flow forecasting.",
    "featureList": [
      "Duplicate payment detection",
      "Automated bank reconciliation",
      "Real-time fraud detection",
      "Predictive cash flow analytics",
      "99.2% matching accuracy",
      "Tally ERP integration"
    ],
    "screenshot": SITE_LOGO
  };
}

export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
    }))
  };
}

export function generateArticleSchema(article: {
  title: string;
  description: string;
  publishedDate: string;
  modifiedDate?: string;
  author?: string;
  image?: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image || SITE_LOGO,
    "author": {
      "@type": "Organization",
      "name": article.author || SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": SITE_LOGO
      }
    },
    "datePublished": article.publishedDate,
    "dateModified": article.modifiedDate || article.publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.url.startsWith('http') ? article.url : `${SITE_URL}${article.url}`
    }
  };
}

export function generatePricingSchema(plans: Array<{
  name: string;
  price: number;
  description: string;
  features: string[];
}>) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${SITE_NAME} Financial Intelligence Platform`,
    "description": "AI-powered financial reconciliation and fraud detection platform",
    "brand": {
      "@type": "Brand",
      "name": SITE_NAME
    },
    "offers": plans.map(plan => ({
      "@type": "Offer",
      "name": plan.name,
      "price": plan.price,
      "priceCurrency": "INR",
      "description": plan.description,
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }))
  };
}

export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareCompany",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": SITE_LOGO,
    "description": "AI-powered financial intelligence platform for Indian businesses",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "20.5937",
      "longitude": "78.9629"
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "priceRange": "₹₹"
  };
}
